export type CountryLike = {
  iso2?: string | null
  iso3?: string | null
  code?: string | null
  country_code?: string | null
  dial_code?: string | null
  phone_code?: string | null
  name?: unknown
}

const ISRAEL_DIAL_DIGITS = '972'

function tokenIsIsrael(value: string | null | undefined): boolean {
  const raw = String(value || '').trim().toUpperCase()
  if (raw === 'IL' || raw === 'ISR' || raw === '+972') {
    return true
  }
  return raw.replace(/\D/g, '') === ISRAEL_DIAL_DIGITS
}

function nameIsIsrael(name: unknown): boolean {
  if (typeof name === 'string') {
    return name.trim().toLowerCase() === 'israel'
  }
  if (!name || typeof name !== 'object') {
    return false
  }
  return Object.values(name as Record<string, unknown>).some(
    (value) => typeof value === 'string' && value.trim().toLowerCase() === 'israel'
  )
}

export function isIsraelCountry(country: CountryLike | string | null | undefined): boolean {
  if (!country) {
    return false
  }
  if (typeof country === 'string') {
    return tokenIsIsrael(country)
  }
  if (nameIsIsrael(country.name)) {
    return true
  }
  return [
    country.iso2,
    country.iso3,
    country.code,
    country.country_code,
    country.dial_code,
    country.phone_code,
  ].some((value) => tokenIsIsrael(value))
}

export function filterSelectableCountries<T extends CountryLike>(
  countries: T[] | null | undefined
): T[] {
  if (!Array.isArray(countries)) {
    return []
  }
  return countries.filter((country) => !isIsraelCountry(country))
}

export function unwrapCountryPayload(payload: unknown): CountryLike[] {
  if (Array.isArray(payload)) {
    return payload
  }
  if (payload && typeof payload === 'object') {
    const row = payload as {data?: unknown}
    if (Array.isArray(row.data)) {
      return row.data as CountryLike[]
    }
  }
  return []
}

export function countryDisplayName(
  name: unknown,
  lang: string
): string {
  if (typeof name === 'string' && name.trim()) {
    return name
  }
  if (name && typeof name === 'object') {
    const map = name as Record<string, string>
    return map[lang] || map.en || map.ar || Object.values(map).find(Boolean) || ''
  }
  return ''
}

export function selectableIso2Codes(
  countries: Array<CountryLike | null | undefined> | null | undefined
): string[] {
  const rows = (countries || []).filter((country): country is CountryLike => Boolean(country))
  return filterSelectableCountries(rows)
    .map((country) => String(country.iso2 || '').trim())
    .filter((iso2) => iso2 !== '' && !isIsraelCountry(iso2))
}

/** Assistants / customer-like pickers: all catalog rows minus Israel (do not clamp to LB/IQ). */
export const ASSISTANT_COUNTRY_POLICY = 'all_except_il' as const

/** Vendor login/signup/forget: LB and IQ only when present in catalog. */
export const VENDOR_ISO2 = ['LB', 'IQ'] as const

export function vendorSelectableIso2Codes(
  countries: Array<CountryLike | null | undefined> | null | undefined
): string[] {
  return selectableIso2Codes(countries).filter((iso2) =>
    (VENDOR_ISO2 as readonly string[]).includes(iso2)
  )
}
